/**
 * Desktop "set new password" (lane D1; INVENTORY-DESKTOP §3.2, prototype
 * template lines 61-70).
 *
 * Rendered by `DesktopApp` WITHOUT the top nav (`headerVM().isReset`). Reached
 * two ways since D-08 — `doLogin()` on a `mustReset` account, and `boot()` on a
 * password-reset link — and the screen never decides which for itself: the
 * heading and body come from `loginVM()`, which reads `resetMode`. There is no
 * way back to the grid short of a reload (faithful to the prototype).
 *
 * Every string is SAME as mobile (INVENTORY §3); only the card geometry is
 * desktop: 460px wide, `padding:34px 32px 28px`, title pixel 11px, body mono
 * 11px, and the button at `fs=9 pad="14px 16px"` (mobile: 8.5 / 15px 14px).
 *
 * DEVIATION D-01 (inherited): the length floor is 8 characters and the copy is
 * never hardcoded here — `doReset()` writes `resetErr` from
 * `PASSWORD_MIN_COPY`, and `loginVM()` re-exports it as `resetHint`.
 */

import type { CSSProperties } from 'react';

import { PixelButton } from '../../components/PixelButton.tsx';
import { useQd } from '../../store/index.ts';
import { loginVM } from '../../store/selectors.ts';

const FIELD: CSSProperties = {
  background: 'var(--gt-field)',
  border: '1px solid var(--gt-border-2)',
  color: 'var(--gt-text)',
  fontFamily: 'var(--gt-font-mono)',
  fontSize: '13px',
  padding: '11px 12px',
  outline: 'none',
};

export default function Reset(): JSX.Element {
  const s = useQd();
  const v = loginVM(s);

  return (
    <div
      data-screen-label="Set new password"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div
        style={{
          width: '460px',
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          background: 'var(--gt-card)',
          border: '1px solid var(--gt-border)',
          boxShadow: 'var(--gt-shadow-panel)',
          padding: '34px 32px 28px',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--gt-font-pixel)',
            fontSize: '11px',
            color: 'var(--gt-text)',
            lineHeight: 1.8,
          }}
        >
          {v.resetHead}
        </div>
        <div
          style={{
            fontFamily: 'var(--gt-font-mono)',
            fontSize: '11px',
            lineHeight: 1.7,
            color: 'var(--gt-text-5)',
          }}
        >
          {v.resetBody}
        </div>
        <input
          type="password"
          value={v.rp1}
          onChange={(e) => s.setRp1(e.target.value)}
          placeholder="NEW PASSWORD"
          style={FIELD}
        />
        <input
          type="password"
          value={v.rp2}
          onChange={(e) => s.setRp2(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.repeat) void s.doReset();
          }}
          placeholder="CONFIRM PASSWORD"
          style={FIELD}
        />
        {v.hasResetErr ? (
          <div style={{ fontFamily: 'var(--gt-font-mono)', fontSize: '11px', color: 'var(--acc2)' }}>
            ✕ <span>{v.resetErr}</span>
          </div>
        ) : null}
        <PixelButton
          label={v.resetLbl}
          busy={v.resetBusy}
          onClick={() => void s.doReset()}
          fs={9}
          pad="14px 16px"
          center
        />
      </div>
    </div>
  );
}
